import React from 'react';
import { sourceOptions } from '../constants/source';
import HashtagSourceSelect, {
  HashtagSourceSelectType,
} from '../widgets/HashtagSourceSelect';
import {
  getCategoryCommonBackValue,
  getCategoryCommonDefaultValue,
  getSingleSelectDefaultValue,
  getMultipleSelectDefaultValue
} from '../utils';
import CategorySelect, { CategorySelectType } from '../widgets/CategorySelect';
import { commonCategoryProps } from '../constants/category';
import { allCountryMap, countryOptions } from '../constants/country';
import { TComponentConfig, TOptionsItem } from '../types';
import RegionCountryItem from '@op/components/Snippets/RegionCountryItem';
import HashtagCountryRegionSelect, {
  HashtagCountryRegionSelectType,
} from '../widgets/HashtagCountryRegionSelect';

function filterSourceOptions(options: TOptionsItem[], filterValue: any) {
  return options.filter((item) => {
    if (
      filterValue.time_range === 'last_30_days' &&
      item.value === 'tiktok_gaming'
    ) {
      return false;
    }
    return true;
  });
}

export const HashtagSource: TComponentConfig<HashtagSourceSelectType> = {
  Component: HashtagSourceSelect,
  defaultValue: (filterValue: any, props: HashtagSourceSelectType) => {
    const { valueKeys: key, options, filterOptions } = props;
    const list = filterOptions(options, filterValue);
    return getSingleSelectDefaultValue(filterValue, key, list);
  },
  backValue: (filterValue: any, props: HashtagSourceSelectType) => {
    const { valueKeys: key } = props;
    return { [key]: filterValue[key] };
  },
  props: {
    title: 'Source',
    titleCn: '来源',
    valueKeys: 'source',
    options: sourceOptions,
    filterOptions: filterSourceOptions,
  },
};

export const HashtagCategory: TComponentConfig<CategorySelectType> = {
  Component: CategorySelect,
  defaultValue: getCategoryCommonDefaultValue,
  backValue: getCategoryCommonBackValue,
  requestKey: 'marketing_hub_hashtag_config_time_range_category',
  props: {
    ...commonCategoryProps,
    options: [],
    filterOptions: (options: any, filterValue: any) => {
      const { time_range } = filterValue;
      return (options?.[time_range] || []).map((item: any) => {
        return {
          value: item.category_en.toLocaleLowerCase(),
          text: item.category_en,
          textCn: item.category_zh,
        };
      });
    },
  },
};

/** 处理国家地区 cond 原始数据 → 选项列表 */
export function dealwithCountryRegionData(data: any[]): TOptionsItem[] {
  const countryMap = new Map<string, TOptionsItem>();

  data?.forEach?.((region: any) => {
    if (region?.region_code?.toLocaleUpperCase() === 'ALL') return;

    region.countries.forEach((country: any) => {
      const { country_code, country_en, country_zh } = country;
      if (!countryMap.has(country_code)) {
        countryMap.set(country_code, {
          value: country_code.toLocaleLowerCase(),
          text: country_en,
          textCn: country_zh,
        });
      }
    });
  });

  return Array.from(countryMap.values());
}

/** 与 HashtagCountryRegion.condDealwith 同一套逻辑 */
function condDealwithHashtagSourcesCountryRegion(
  data: unknown,
): TOptionsItem[] | Record<string, TOptionsItem[]> {
  if (!data) return [];

  if (Array.isArray(data)) {
    return dealwithCountryRegionData(data);
  }

  const result: Record<string, TOptionsItem[]> = {};
  Object.keys(data as Record<string, any[]>).forEach((sourceKey) => {
    result[sourceKey] = dealwithCountryRegionData(
      (data as Record<string, any[]>)[sourceKey],
    );
  });
  return result;
}

/**
 * 供 Tab 页直接消费 cond 数据（与 FilterCommon HashtagCountryRegion 一致）
 * @param source cond 分组 key，X Tab 传 `common`
 */
export function getHashtagSourcesCountryRegionOptions(
  condRaw: unknown,
  source = 'common',
): TOptionsItem[] {
  const options = condDealwithHashtagSourcesCountryRegion(condRaw);
  if (Array.isArray(options)) {
    return options;
  }
  return options[source] || options.common || [];
}

export const HashtagCountryRegion: TComponentConfig<HashtagCountryRegionSelectType> = {
  Component: HashtagCountryRegionSelect,
  defaultValue: (filterValue: any, props: HashtagCountryRegionSelectType) => {
    const { valueKeys: key, options } = props;
    // 如果 options 是对象，根据 source 获取对应选项
    const realOptions = options && !Array.isArray(options)
      ? (options as Record<string, TOptionsItem[]>)[filterValue?.source] || []
      : options;
    return getMultipleSelectDefaultValue(filterValue, key, realOptions, []);
  },
  backValue: (filterValue: any, props: HashtagCountryRegionSelectType) => {
    const key = props.valueKeys;
    const value = filterValue[key];
    return { [key]: value.length === 0 ? ['all'] : value };
  },
  condDealwith: (condInfo: any, key: string) => {
    const data = condInfo[key];

    // 如果是对象（按 source 分组），处理每个 source 的数据
    if (data && !Array.isArray(data)) {
      const result: Record<string, TOptionsItem[]> = {};
      Object.keys(data).forEach((source) => {
        result[source] = dealwithCountryRegionData(data[source]);
      });
      return result;
    }

    // 如果是数组，直接处理
    return dealwithCountryRegionData(data);
  },
  requestKey: 'marketing_hub_hashtag_config_sources_country_region',
  props: {
    title: 'Country/Region',
    titleCn: '国家/地区',
    valueKeys: 'countries',
    options: countryOptions,
    leftText: 'Country',
    ...allCountryMap,
    hasSelectAllBtn: true,
    matchButtonWidth: true,
    itemRender: (option) => {
      return (
        <RegionCountryItem
          style={{ display: 'inline-flex', height: 24 }}
          iconStyle={{ width: 16 }}
          market={option.value}
        />
      );
    },
  },
};
